import React, { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  ApiNotificationStatus,
  NotificationDto,
  notificationApi,
} from "../../../../services/notificationApi";
import {
  formatNotificationDate,
  notificationStatus,
  notificationTarget,
} from "./notificationPresentation";
import styles from "./notifications.module.scss";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const NotificationsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | ApiNotificationStatus>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    notificationApi
      .list({
        page,
        pageSize,
        status: statusFilter === "all" ? undefined : statusFilter,
        searchTerm: debouncedSearch,
      })
      .then((response) => {
        if (cancelled) return;
        const results = response.results ?? [];
        const records = response.totalNumberOfRecords ?? results.length;
        const pages = response.totalNumberOfPages ?? Math.ceil(records / pageSize);
        setNotifications(results);
        setTotalRecords(records);
        setTotalPages(Math.max(1, pages));
        if (pages > 0 && page > pages) setPage(pages);
      })
      .catch(() => {
        if (cancelled) return;
        setNotifications([]);
        setTotalPages(1);
        setTotalRecords(0);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedSearch, page, pageSize, statusFilter]);

  const pageNumbers = Array.from(new Set([1, page - 1, page, page + 1, totalPages]))
    .filter((number) => number >= 1 && number <= totalPages)
    .sort((left, right) => left - right);
  const firstRecord = (page - 1) * pageSize + 1;
  const lastRecord = Math.min(page * pageSize, totalRecords);

  return (
    <div className={styles["notifications-dashboard"]}>
      <div className={styles["notifications-dashboard__heading-row"]}>
        <div>
          <h2>Notifications</h2>
          <p>Send and track campaigns to customers and vendors.</p>
        </div>
        <button type="button" className={styles["notification-primary-button"]} onClick={() => navigate("/app/settings/notifications/new")}>New notification</button>
      </div>

      <div className={styles["notification-filters"]} aria-label="Notification filters">
        <select aria-label="Filter by type (unavailable)" title="Notification type is not provided by the API" disabled>
          <option>All types</option>
        </select>
        <select aria-label="Filter by audience (unavailable)" title="Audience app is not provided by the API" disabled>
          <option>All audiences</option>
        </select>
        <select
          value={statusFilter}
          onChange={(event) => {
            setPage(1);
            setStatusFilter(event.target.value === "all" ? "all" : Number(event.target.value) as ApiNotificationStatus);
          }}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value={ApiNotificationStatus.Sent}>Sent</option>
          <option value={ApiNotificationStatus.Sending}>Sending</option>
          <option value={ApiNotificationStatus.Scheduled}>Scheduled</option>
          <option value={ApiNotificationStatus.Draft}>Draft</option>
          <option value={ApiNotificationStatus.Failed}>Failed</option>
        </select>
        <label className={styles["notification-search"]}>
          <FiSearch aria-hidden="true" />
          <input
            value={searchTerm}
            onChange={(event) => {
              setPage(1);
              setSearchTerm(event.target.value);
            }}
            placeholder="Search by title"
            aria-label="Search notifications by title"
          />
        </label>
      </div>

      <div className={styles["notification-table-wrap"]}>
        <table className={styles["notification-table"]}>
          <thead><tr><th>Title</th><th>Segment</th><th>Audience</th><th>Status</th><th>Sent / Scheduled</th><th>Delivered / Opened</th></tr></thead>
          <tbody>
            {!loading && !error && notifications.map((notification) => {
              const status = notificationStatus(notification.status);
              return (
                <tr key={notification.id}>
                  <td><button type="button" className={styles["notification-title-link"]} onClick={() => navigate(`/app/settings/notifications/${notification.id}`)}>{notification.title || "Untitled notification"}</button></td>
                  <td>{notificationTarget(notification.targetType)}</td>
                  <td title="Audience app is not provided by the API">—</td>
                  <td><span className={`${styles["notification-status"]} ${styles[`notification-status--${status.className}`] || ""}`}>{status.label}</span></td>
                  <td>{formatNotificationDate(notification.sentAt || notification.scheduledAt || notification.createdDate)}</td>
                  <td>{notification.status === ApiNotificationStatus.Sent ? `${notification.deliveredCount} / ${notification.openedCount}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading && <div className={styles["notification-empty-state"]} role="status">Loading notifications…</div>}
        {error && <div className={styles["notification-empty-state"]} role="alert">Could not load notifications. Please try again later.</div>}
        {!loading && !error && notifications.length === 0 && <div className={styles["notification-empty-state"]}><strong>No notifications found</strong><span>Try changing the status or search phrase.</span></div>}
      </div>

      {!loading && !error && totalRecords > 0 && (
        <div className={styles["notification-pagination"]} aria-label="Notification table pagination">
          <span className={styles["notification-pagination__summary"]}>Showing {firstRecord}–{lastRecord} of {totalRecords}</span>
          <label className={styles["notification-pagination__page-size"]}>
            Rows per page
            <select
              value={pageSize}
              onChange={(event) => {
                setPage(1);
                setPageSize(Number(event.target.value));
              }}
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
          <div className={styles["notification-pagination__controls"]}>
            <button type="button" onClick={() => setPage((current) => current - 1)} disabled={page <= 1}>Previous</button>
            {pageNumbers.map((number, index) => (
              <React.Fragment key={number}>
                {index > 0 && number - pageNumbers[index - 1] > 1 && <span className={styles["notification-pagination__ellipsis"]} aria-hidden="true">…</span>}
                <button
                  type="button"
                  className={`${styles["notification-pagination__page"]} ${number === page ? styles["notification-pagination__page--current"] : ""}`}
                  aria-label={`Page ${number}`}
                  aria-current={number === page ? "page" : undefined}
                  onClick={() => setPage(number)}
                  disabled={number === page}
                >
                  {number}
                </button>
              </React.Fragment>
            ))}
            <button type="button" onClick={() => setPage((current) => current + 1)} disabled={page >= totalPages}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDashboard;
